import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que nos proporcionas voluntariamente al realizar una compra, como tu nombre, dirección de correo electrónico y datos de pago. No compartimos esta información con terceros, excepto cuando sea necesario para procesar tu pedido.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos tu información personal para: procesar y entregar tu compra, enviarte actualizaciones relacionadas con tu producto, mejorar nuestros servicios y comunicarnos contigo sobre ofertas relevantes (solo si has dado tu consentimiento).'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, contáctanos a través de los canales proporcionados.'
  }, {
    heading: 'Cambios a esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, aceptas cumplir con estos términos y condiciones. Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices nuestros servicios.'
  }, {
    heading: 'Producto Digital',
    text: 'El producto "Adiós Pañal en 3 Días" es una guía digital en formato PDF. Al completar tu compra, recibirás acceso inmediato al material. Este es un producto informativo y educativo basado en experiencias y métodos probados.'
  }, {
    heading: 'Resultados',
    text: 'Los resultados pueden variar según cada niño y familia. Si bien nuestro método ha sido exitoso para más de 500 familias, no garantizamos resultados específicos ya que cada situación es única. El éxito depende del seguimiento correcto del protocolo.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido de este sitio web y del producto digital, incluyendo textos, gráficos, logotipos y materiales descargables, está protegido por derechos de autor. Queda prohibida su reproducción, distribución o modificación sin autorización previa.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Sin embargo, si experimentas problemas técnicos para acceder al material, nos comprometemos a resolverlos de inmediato.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'Este producto es una guía educativa y no sustituye el consejo médico profesional. Para niños con condiciones médicas específicas, consulta con tu pediatra antes de implementar cualquier método de entrenamiento.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-dark-teal text-white/60 py-8 text-center text-sm" data-id="element-36">
        <div className="max-w-7xl mx-auto px-4" data-id="element-37">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 mb-3" data-id="element-38">
            <button onClick={() => setActiveModal('privacy')} className="text-white/70 hover:text-white transition-colors underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral rounded" data-id="element-39">
              Política de Privacidad
            </button>
            <span className="hidden sm:inline text-white/30" data-id="element-40">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-white/70 hover:text-white transition-colors underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral rounded" data-id="element-41">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-42">
            © {new Date().getFullYear()} Adiós Pañal en 3 Días. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-43">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-44">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-45">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0" data-id="element-46">
                <h2 className="text-2xl font-heading font-black text-dark-teal" data-id="element-47">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral" aria-label="Cerrar" data-id="element-48">
                  <X size={20} className="text-gray-600" data-id="element-49" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto p-6 space-y-6" data-id="element-50">
                {content.sections.map((section, idx) => <div key={idx} data-id="element-51">
                    <h3 className="text-lg font-heading font-bold text-dark-teal mb-2" data-id="element-52">
                      {section.heading}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-53">
                      {section.text}
                    </p>
                  </div>)}

                <p className="text-sm text-gray-400 pt-4 border-t border-gray-100" data-id="element-54">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}